import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import type {Component, Editor} from 'grapesjs';
import type {FieldMeta, FormMakerActions, FormMakerContextProps, FormMakerImmutableStore, FormMakerState, SaveOptions} from '@FormMakerReact/form-maker.types';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {CanvasDevice, DEFAULT_FIELDS_META, EditorViewport, FormMakerFormMode, RightPanelView} from '@FormMakerReact/form-maker.types';
import {
  canRedo,
  canUndo,
  redo as canvasRedo,
  selectComponent as canvasSelectComponent,
  setCanvasDevice as canvasSetDevice,
  undo as canvasUndo,
} from '@FormMakerReact/lib/form-maker-canvas-library';
import {
  destroyEditor,
  hideFormMakerPagePreloader,
  loadFormIntoEditor,
  mountEditor,
  startFormMakerPreloaderTipRotation,
  toggleFormVisibility,
  waitForCanvasFrameRendered,
  waitForDirtyCountToSettle,
  waitForEditorLoad,
} from '@FormMakerReact/lib/form-maker-library';
import {
  backfillErrorBoxDefaultCopy,
  ensureSubmitButtonContainerBoxSizing,
  ensureWrapperHorizontalPadding,
  migrateCssRulesToInlineStyles,
  moveTrailersToEndOfEmailForm,
  reattachOrphanedFormBackground,
  reconcileCustomFieldLabelsToCanvas,
  setFormsWrapperSignupFormId,
  stripBogusRequiredAttributes,
  stripHeadingTextMinHeight,
} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import type {StoreOpts} from '@FormMakerReact/grapesjs-form-maker-plugin/src/form-maker-storage';
import {FIELDS_META_MODEL_KEY, STORE_OPTS_MODEL_KEY} from '@FormMakerReact/grapesjs-form-maker-plugin/src/form-maker-storage';
import {registerDescriptionRteActions} from '@FormMakerReact/grapesjs-form-maker-plugin/src/description-rte/description-rte-actions';
import type {SaveFormResponse} from '@FormMakerReact/lib/form-maker-storage-library';
import {ConcurrentEditError, SaveActionType, SaveFormRejectedError, SaveType, SignupFormLimitExceededError, isSignupFormGateRejection} from '@FormMakerReact/lib/form-maker-storage-library';
import {showMessageGrowl} from '@Components/message-growl/message-growl';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {openFormMakerConflictModal} from '@FormMakerReact/lib/form-maker-conflict-library';
import {mergeIdUserHasCustomFieldFromResponse} from '@FormMakerReact/lib/form-maker-fields-meta-library';
import {adoptDraftInPlace, leaveDraftForPublishWizard} from '@FormMakerReact/lib/form-maker-access-library';

export interface UseFormMakerContextStateArgs {
  store: FormMakerImmutableStore;
  canvasContainer: HTMLElement | null;
}

interface EditorRef {
  current: Editor | null;
}

const AUTOSAVE_DEBOUNCE_MS = 1500;

declare global {
  interface Window {
    pmwFormMakerEditor?: Editor;
  }
}

export const INITIAL_STATE: FormMakerState = {
  hasUnsavedChanges: false,
  isSaving: false,
  saveFailed: false,
  saveErrorMessage: null,
  isEditorLoading: true,
  lastSavedAt: null,
  selectedComponent: null,
  editorViewport: EditorViewport.DESKTOP,
  canvasDevice: CanvasDevice.DESKTOP,
  previewMode: false,
  fieldsMeta: DEFAULT_FIELDS_META.map((f) => ({...f})),
  formMode: FormMakerFormMode.SIGNUP,
  rightPanelView: RightPanelView.DEFAULT,
};

export function useFormMakerContextState(args: UseFormMakerContextStateArgs): FormMakerContextProps {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [state, setState] = useState<FormMakerState>(INITIAL_STATE);

  useEditorViewport(setState);

  useEffect(() => {
    return startFormMakerPreloaderTipRotation();
  }, []);

  const editorRef = useRef<Editor | null>(null);
  const stateRef = useRef<FormMakerState>(state);
  stateRef.current = state;

  const isHydratedRef = useRef<boolean>(false);
  const storeAttemptRef = useRef<StoreAttempt>({hasFailed: false, dirtyCountAtStart: 0});

  const {effectiveIdEmailSignupForm, effectiveIdRef, autosaveEnabled, adopt} = useDraftAdoption(args.store, editorRef);

  useEffect(() => {
    if (!args.canvasContainer || editorRef.current) {
      return undefined;
    }
    const e = mountEditor({container: args.canvasContainer});
    editorRef.current = e;
    setEditor(e);
    wireEditorListeners(e, setState, isHydratedRef, storeAttemptRef);
    registerDescriptionRteActions(e);
    exposeEditorGlobal(e);

    waitForEditorLoad(e)
      .then(() => bootstrapEditor(e, args.store.idEmailSignupForm, isHydratedRef, setState))
      .catch((err: unknown) => {
        console.error('Form maker editor load failed', err);
      })
      .finally(() => {
        hideFormMakerPagePreloader();
      });

    return (): void => {
      destroyEditor(e);
      editorRef.current = null;
      setEditor(null);
      isHydratedRef.current = false;
      clearEditorGlobal();
    };
  }, [args.canvasContainer, args.store.idEmailSignupForm]);

  const onSaveResponse = useCallback(
    (response: SaveFormResponse, saveType: SaveType): void => {
      handleSaveResponse({response, saveType, wasPersisted: effectiveIdRef.current !== '', setState, onAdopt: adopt});
    },
    [adopt, effectiveIdRef]
  );

  const save = useCallback(
    async (opts?: SaveOptions): Promise<boolean> => {
      if (!editor) {
        return false;
      }
      try {
        await editor.store(buildStoreOpts(effectiveIdRef.current, args.store.name, stateRef.current.fieldsMeta, opts, onSaveResponse));
        return true;
      } catch (err) {
        return handleSaveError(err, (retryOpts) => save(retryOpts), opts?.saveType);
      }
    },
    [editor, args.store.name, onSaveResponse, effectiveIdRef]
  );

  useDebouncedAutosave(editor, state.hasUnsavedChanges && autosaveEnabled, state.isSaving, save, isHydratedRef);
  useBeforeUnloadGuard(stateRef);
  useStashStoreOptsOnEditor(editor, {idEmailSignupForm: effectiveIdEmailSignupForm, name: args.store.name, fieldsMeta: state.fieldsMeta, onSaveResponse});

  const actions = useFormMakerActions(editor, save, setState);

  const store = useMemo<FormMakerImmutableStore>(
    () => ({...args.store, idEmailSignupForm: effectiveIdEmailSignupForm, canAutosave: autosaveEnabled}),
    [args.store, effectiveIdEmailSignupForm, autosaveEnabled]
  );

  return useMemo(() => ({editor, store, state, actions}), [editor, store, state, actions]);
}

/** Keeps state.editorViewport in sync with the responsive breakpoint. */
function useEditorViewport(setState: SetStateFn): void {
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.SMALL);
  const isTablet = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.TABLET);
  const editorViewport: EditorViewport = isMobile ? EditorViewport.MOBILE : isTablet ? EditorViewport.TABLET : EditorViewport.DESKTOP;
  useEffect(() => {
    setState((s) => (s.editorViewport === editorViewport ? s : {...s, editorViewport}));
  }, [editorViewport, setState]);
}

interface DraftAdoption {
  effectiveIdEmailSignupForm: string;
  effectiveIdRef: {current: string};
  autosaveEnabled: boolean;
  adopt: (idEmailSignupForm: string, saveType: SaveType) => void;
}

/**
 * A draft (empty `idEmailSignupForm`) receives its real id from the first save and adopts it without
 * a reload. Exposes the effective id (the prop's id, or the adopted one), whether autosave is on for
 * the session, and the `adopt` callback the save flow calls once the server mints the id. The mount
 * effect stays keyed on the prop's original id, so adoption never remounts the editor.
 */
function useDraftAdoption(store: FormMakerImmutableStore, editorRef: EditorRef): DraftAdoption {
  const [adoptedId, setAdoptedId] = useState<string>('');
  const [autosaveEnabled, setAutosaveEnabled] = useState<boolean>(store.canAutosave);
  const effectiveIdEmailSignupForm = store.idEmailSignupForm || adoptedId;
  const effectiveIdRef = useRef<string>(effectiveIdEmailSignupForm);
  effectiveIdRef.current = effectiveIdEmailSignupForm;

  const adopt = useCallback(
    (idEmailSignupForm: string, saveType: SaveType): void => {
      if (saveType === SaveType.HARD) {
        leaveDraftForPublishWizard(idEmailSignupForm);
        return;
      }
      persistIdOntoCanvasWrapper(editorRef.current, idEmailSignupForm);
      setAdoptedId(idEmailSignupForm);
      setAutosaveEnabled(true);
      adoptDraftInPlace(idEmailSignupForm);
    },
    [editorRef]
  );

  return {effectiveIdEmailSignupForm, effectiveIdRef, autosaveEnabled, adopt};
}

/** Puts the minted id on the canvas form wrapper so the next autosave persists HTML carrying it — what the pre-adoption reload used to do. */
function persistIdOntoCanvasWrapper(editor: Editor | null, idEmailSignupForm: string): void {
  if (editor) {
    setFormsWrapperSignupFormId(editor, idEmailSignupForm);
  }
}

function buildStoreOpts(idEmailSignupForm: string, name: string, fieldsMeta: FieldMeta[], opts: SaveOptions | undefined, onSaveResponse: StoreOpts['onSaveResponse']): StoreOpts {
  return {
    idEmailSignupForm,
    name,
    fieldsMeta,
    saveActionType: opts?.saveActionType ?? SaveActionType.ATTEMPT,
    saveType: opts?.saveType,
    onSaveResponse,
  };
}

/**
 * The gate check and the storage:* listeners have already put the editor into the right state, so
 * every branch here just stops the save chain: a gate rejection is silent, a concurrent edit opens
 * the conflict modal, an at-limit save shows its growl, and anything else is logged for the failed badge.
 */
function handleSaveError(err: unknown, retrySave: (opts: SaveOptions) => Promise<boolean>, saveType: SaveType | undefined): boolean {
  if (isSignupFormGateRejection(err)) {
    return false;
  }
  if (err instanceof ConcurrentEditError) {
    openFormMakerConflictModal({
      payload: err.payload,
      onAcceptYours: (): void => {
        void retrySave({saveActionType: SaveActionType.FORCE, saveType});
      },
    });
    return false;
  }

  if (err instanceof SaveFormRejectedError) {
    return false;
  }

  if (err instanceof SignupFormLimitExceededError) {
    showMessageGrowl({text: err.message, type: GROWL_TYPE.DANGER});
    return false;
  }

  window.PMW.log('Signup form save failed', err);
  return false;
}

function useFormMakerActions(editor: Editor | null, save: (opts?: SaveOptions) => Promise<boolean>, setState: SetStateFn): FormMakerActions {
  return useMemo(
    () => ({
      save,
      undo: (): void => {
        if (editor && canUndo(editor)) {
          canvasUndo(editor);
        }
      },
      redo: (): void => {
        if (editor && canRedo(editor)) {
          canvasRedo(editor);
        }
      },
      setPreviewMode: (next: boolean): void => {
        setState((s) => ({...s, previewMode: next}));
      },
      setCanvasDevice: (device: CanvasDevice): void => {
        setState((s) => ({...s, canvasDevice: device}));
        if (editor) {
          canvasSetDevice(editor, device);
        }
      },
      selectComponent: (component: Component | null): void => {
        if (editor) {
          canvasSelectComponent(editor, component);
        }
      },
      setFieldsMeta: (updater: (prev: FieldMeta[]) => FieldMeta[]): void => {
        setState((s) => ({...s, fieldsMeta: updater(s.fieldsMeta)}));
      },
      setFormMode: (mode: FormMakerFormMode): void => {
        setState((s) => ({...s, formMode: mode}));
        if (editor) {
          toggleFormVisibility(editor, mode);
        }
      },
      openRightPanelKeyboardShortcuts: (): void => {
        setState((s) => (s.rightPanelView === RightPanelView.KEYBOARD_SHORTCUTS ? s : {...s, rightPanelView: RightPanelView.KEYBOARD_SHORTCUTS}));
      },
      closeRightPanelOverlay: (): void => {
        setState((s) => (s.rightPanelView === RightPanelView.DEFAULT ? s : {...s, rightPanelView: RightPanelView.DEFAULT}));
      },
    }),
    [editor, save, setState]
  );
}

export type SetStateFn = (updater: (s: FormMakerState) => FormMakerState) => void;

export interface StoreAttempt {
  hasFailed: boolean;
  /** GrapesJS's dirty count when the store started; anything above it at storage:end:store landed mid-flight. */
  dirtyCountAtStart: number;
}

export interface HandleSaveResponseArgs {
  response: SaveFormResponse;
  saveType: SaveType;
  /** Whether the form already had an id before this save; `false` means this save adopted a draft. */
  wasPersisted: boolean;
  setState: SetStateFn;
  onAdopt: (idEmailSignupForm: string, saveType: SaveType) => void;
}

/**
 * Runs for every successful store. When a draft's first save created the real form, the state is
 * marked clean synchronously — so useBeforeUnloadGuard cannot prompt over what was just persisted —
 * before the caller adopts the new id.
 */
export function handleSaveResponse({response, saveType, wasPersisted, setState, onAdopt}: HandleSaveResponseArgs): void {
  setState((s) => ({...s, fieldsMeta: mergeIdUserHasCustomFieldFromResponse(s.fieldsMeta, response.fieldsMeta)}));
  if (wasPersisted) {
    return;
  }
  flushSync((): void => {
    setState((s) => ({...s, hasUnsavedChanges: false, isSaving: false}));
  });
  onAdopt(response.idEmailSignupForm, saveType);
}

export function wireEditorListeners(editor: Editor, setState: SetStateFn, isHydratedRef: {current: boolean}, storeAttemptRef: {current: StoreAttempt}): void {
  editor.on('component:selected', (component: Component) => {
    setState((s) => {
      if (s.selectedComponent === component && s.rightPanelView === RightPanelView.DEFAULT) {
        return s;
      }
      return {...s, selectedComponent: component, rightPanelView: RightPanelView.DEFAULT};
    });
  });
  editor.on('component:deselected', () => {
    setState((s) => (s.selectedComponent === null ? s : {...s, selectedComponent: null}));
  });
  editor.on('change:changesCount', () => {
    onChangesCount(editor, setState, isHydratedRef);
  });
  editor.on('storage:start:store', () => {
    storeAttemptRef.current = {hasFailed: false, dirtyCountAtStart: editor.getDirtyCount()};
    setState((s) => ({...s, isSaving: true, saveFailed: false, saveErrorMessage: null}));
  });
  editor.on('storage:end:store', () => {
    if (storeAttemptRef.current.hasFailed) {
      return;
    }
    setState((s) => ({...s, isSaving: false, hasUnsavedChanges: editedWhileStoring(editor, storeAttemptRef.current), lastSavedAt: Date.now()}));
  });
  editor.on('storage:error', (err: unknown) => {
    storeAttemptRef.current.hasFailed = true;
    const saveErrorMessage = err instanceof SaveFormRejectedError ? err.message : null;
    setState((s) => ({...s, isSaving: false, saveFailed: !isSaveErrorShownElsewhere(err), saveErrorMessage}));
  });
}

/** GrapesJS counts a mid-flight edit and only resets the count after storage:end:store, so a count above the start means an edit landed while the store was in flight. */
function editedWhileStoring(editor: Editor, attempt: StoreAttempt): boolean {
  return editor.getDirtyCount() > attempt.dirtyCountAtStart;
}

/** A gate rejection (login/upsell modal) and a form-limit rejection (its own growl) are already shown to the user, so the badge stays "unsaved" rather than the red "failed". */
function isSaveErrorShownElsewhere(err: unknown): boolean {
  return isSignupFormGateRejection(err) || err instanceof SignupFormLimitExceededError;
}

function onChangesCount(editor: Editor, setState: SetStateFn, isHydratedRef: {current: boolean}): void {
  if (!isHydratedRef.current || isDirtyCountReset(editor)) {
    return;
  }
  setState((s) => (s.hasUnsavedChanges ? s : {...s, hasUnsavedChanges: true}));
}

/** GrapesJS fires change:changesCount when it clears the count to 0 after a store; that reset is not a user edit. */
function isDirtyCountReset(editor: Editor): boolean {
  return editor.getDirtyCount() === 0;
}

function exposeEditorGlobal(editor: Editor): void {
  window.pmwFormMakerEditor = editor;
}

function clearEditorGlobal(): void {
  delete window.pmwFormMakerEditor;
}

async function bootstrapEditor(editor: Editor, idEmailSignupForm: string, isHydratedRef: {current: boolean}, setState: SetStateFn): Promise<void> {
  await loadFormIntoEditor(editor, idEmailSignupForm);

  reattachOrphanedFormBackground(editor);
  migrateCssRulesToInlineStyles(editor);
  stripBogusRequiredAttributes(editor);
  ensureSubmitButtonContainerBoxSizing(editor);
  ensureWrapperHorizontalPadding(editor);
  stripHeadingTextMinHeight(editor);
  backfillErrorBoxDefaultCopy(editor);
  moveTrailersToEndOfEmailForm(editor);
  setFormsWrapperSignupFormId(editor, idEmailSignupForm);

  toggleFormVisibility(editor, FormMakerFormMode.SIGNUP);

  const loadedFieldsMeta = editor.getModel().get(FIELDS_META_MODEL_KEY) as FieldMeta[] | undefined;
  if (loadedFieldsMeta) {
    reconcileCustomFieldLabelsToCanvas(editor, loadedFieldsMeta);
    setState((s) => ({...s, fieldsMeta: loadedFieldsMeta}));
  }

  await settleHydrationBaseline(editor);
  isHydratedRef.current = true;
  setState((s) => (!s.hasUnsavedChanges && !s.isEditorLoading ? s : {...s, hasUnsavedChanges: false, isEditorLoading: false}));
}

/**
 * The clean baseline — nothing to undo, nothing unsaved. Waited for the canvas to render first:
 * rendering the seeded components mutates the dirty count on a zero-delay timer, so taking the
 * baseline earlier would leave an untouched draft reading as "unsaved".
 */
async function settleHydrationBaseline(editor: Editor): Promise<void> {
  await waitForCanvasFrameRendered(editor);
  await waitForDirtyCountToSettle();
  editor.UndoManager.clear();
  editor.clearDirtyCount();
}

function useDebouncedAutosave(editor: Editor | null, hasUnsavedChanges: boolean, isSaving: boolean, save: (opts?: SaveOptions) => Promise<boolean>, isHydratedRef: {current: boolean}): void {
  useEffect((): (() => void) | undefined => {
    if (!editor || !isHydratedRef.current || !hasUnsavedChanges || isSaving) {
      return undefined;
    }
    const timer = window.setTimeout((): void => {
      void save();
    }, AUTOSAVE_DEBOUNCE_MS);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [editor, hasUnsavedChanges, isSaving, save, isHydratedRef]);
}

function useBeforeUnloadGuard(stateRef: {current: FormMakerState}): void {
  useEffect((): (() => void) => {
    const handler = (event: BeforeUnloadEvent): void => {
      if (!stateRef.current.hasUnsavedChanges) {
        return;
      }
      event.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return (): void => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [stateRef]);
}

/**
 * The one writer of the Ctrl+S / embedded-Done stash. It carries no saveType or saveActionType:
 * those are per-call intents (Publish's HARD, the conflict modal's FORCE) that a later Ctrl+S must not inherit.
 */
function useStashStoreOptsOnEditor(editor: Editor | null, stash: Required<Pick<StoreOpts, 'idEmailSignupForm' | 'name' | 'fieldsMeta' | 'onSaveResponse'>>): void {
  const {idEmailSignupForm, name, fieldsMeta, onSaveResponse} = stash;
  useEffect((): void => {
    if (!editor) {
      return;
    }
    const storeOpts: StoreOpts = {idEmailSignupForm, name, fieldsMeta, onSaveResponse};
    editor.getModel().set(STORE_OPTS_MODEL_KEY, storeOpts);
  }, [editor, idEmailSignupForm, name, fieldsMeta, onSaveResponse]);
}
